import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CredentialResponse, GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { GoogleLoginButton } from 'react-social-login-buttons';
import { googleCredentialToProfile } from '../../utils/googleCredential';

type GoogleSocialLoginButtonProps = {
  clientId: string;
  buttonText?: string;
  onLoginStart: () => void;
  onSuccess: (profile: NonNullable<ReturnType<typeof googleCredentialToProfile>>) => void;
  onError?: (err?: unknown) => void;
};

function GoogleSocialLoginButtonInner({
  buttonText,
  onLoginStart,
  onSuccess,
  onError,
}: Omit<GoogleSocialLoginButtonProps, 'clientId'>) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [btnWidth, setBtnWidth] = useState(0);

  useEffect(() => {
    const el = hostRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const updateWidth = () => {
      const width = Math.round(el.getBoundingClientRect().width);
      if (width > 0) setBtnWidth(Math.max(width, 200));
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleSuccess = useCallback(
    (credentialResponse: CredentialResponse) => {
      const profile = googleCredentialToProfile(credentialResponse?.credential || '');
      if (!profile) {
        onError?.(new Error('Invalid Google credential'));
        return;
      }
      onSuccess(profile);
    },
    [onSuccess, onError],
  );

  return (
    <div
      ref={hostRef}
      className="social-google-login-host"
      style={{ position: 'relative', width: '100%', minHeight: 40 }}
    >
      {/* Original app button styling — visual only */}
      <div style={{ pointerEvents: 'none' }}>
        <GoogleLoginButton text={buttonText} />
      </div>

      {/* Transparent Google iframe on top — mount only once host width is known */}
      {btnWidth > 0 ? (
        <div
          className="social-google-login-layer"
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0.01,
            cursor: 'pointer',
          }}
        >
          <GoogleLogin
            onSuccess={handleSuccess}
            onError={() => onError?.()}
            click_listener={onLoginStart}
            text={buttonText ? 'continue_with' : 'signin_with'}
            width={btnWidth}
            size="large"
            shape="pill"
            theme="outline"
            ux_mode="popup"
            containerProps={{
              style: {
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              },
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

function GoogleSocialLoginButton({
  clientId,
  buttonText,
  onLoginStart,
  onSuccess,
  onError,
}: GoogleSocialLoginButtonProps) {
  if (!clientId) return null;

  return (
    <GoogleOAuthProvider clientId={clientId}>
      <GoogleSocialLoginButtonInner
        buttonText={buttonText}
        onLoginStart={onLoginStart}
        onSuccess={onSuccess}
        onError={onError}
      />
    </GoogleOAuthProvider>
  );
}

export default GoogleSocialLoginButton;
